import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  makeStyles,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import PostAddIcon from '@material-ui/icons/PostAdd';
import UpdateIcon from '@material-ui/icons/Update';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import PrintIcon from '@material-ui/icons/Print';
import { formattedCurrency } from '../../../utils/utils';

const useStyles = makeStyles((theme) => ({
  metricCard: {
    padding: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  metricIcon: {
    padding: theme.spacing(1.2),
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  summaryPaper: {
    padding: theme.spacing(3),
    borderRadius: 10,
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    marginTop: theme.spacing(2.5),
  },
  labelPaper: {
    padding: theme.spacing(2.5),
    borderRadius: 10,
    backgroundColor: '#f8fbff',
    border: '1px solid #d4e5f9',
    marginTop: theme.spacing(2.5),
    boxShadow: '0 1px 3px 0 rgba(23, 73, 127, 0.04)',
  },
  printBtn: {
    fontWeight: 600,
    backgroundColor: '#17497F',
    color: '#ffffff',
    padding: '8px 22px',
    borderRadius: 8,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#123863',
    },
  },
}));

const ImportStepSummary = ({
  products = [],
  resolutions = {},
  excludedIndices = new Set(),
  onBackToReview,
  onConfirmImport,
  printAfterConfirm = true,
  onTogglePrintAfterConfirm,
  confirming = false,
}) => {
  const classes = useStyles();

  // Consolidação de métricas
  let newProductsCount = 0;
  let addStockCount = 0;
  let childFormatsCount = 0;
  let totalCostSum = 0;
  let totalPotentialSale = 0;

  products.forEach((p, index) => {
    if (excludedIndices.has(index)) return;

    const res = resolutions[index] || {};
    const mode = res.conversionMode || (p.isPackage ? 'FRACTION_ONLY' : 'SIMPLE');
    const itemsPerUnit = res.itemsPerUnit || p.suggestedItemsPerUnit || 1;

    let finalAmount = p.amount;
    let finalCost = p.cost;

    if (mode === 'FRACTION_ONLY') {
      finalAmount = p.amount * itemsPerUnit;
      finalCost = p.cost / itemsPerUnit;
    }

    const action = res.action || (p.analysis?.status === 'EXISTING' ? 'ADD' : 'NEW');
    if (action === 'ADD') {
      addStockCount++;
    } else {
      newProductsCount++;
    }

    const childFormats = res.childFormats || [];
    childFormatsCount += childFormats.length;

    totalCostSum += finalAmount * finalCost;
    const saleVal = res.value || (finalCost * 1.3);
    totalPotentialSale += finalAmount * saleVal;
  });

  const totalItems = newProductsCount + addStockCount;

  return (
    <Box>
      <Typography variant="h6" style={{ fontWeight: 600, color: '#17497F', marginBottom: 16 }}>
        Resumo da Entrada de Produtos
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <Paper className={classes.metricCard} elevation={0}>
            <div className={classes.metricIcon} style={{ backgroundColor: '#e0f2fe', color: '#0369a1' }}>
              <PostAddIcon />
            </div>
            <Box>
              <Typography variant="h5" style={{ fontWeight: 700, lineHeight: 1.1 }}>
                {newProductsCount}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Novos Produtos
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={6} md={3}>
          <Paper className={classes.metricCard} elevation={0}>
            <div className={classes.metricIcon} style={{ backgroundColor: '#fef3c7', color: '#b45309' }}>
              <UpdateIcon />
            </div>
            <Box>
              <Typography variant="h5" style={{ fontWeight: 700, lineHeight: 1.1 }}>
                {addStockCount}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Estoque Atualizado
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={6} md={3}>
          <Paper className={classes.metricCard} elevation={0}>
            <div className={classes.metricIcon} style={{ backgroundColor: '#eff6ff', color: '#17497F' }}>
              <AccountTreeIcon />
            </div>
            <Box>
              <Typography variant="h5" style={{ fontWeight: 700, lineHeight: 1.1 }}>
                {childFormatsCount}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Formatos / Frações
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={6} md={3}>
          <Paper className={classes.metricCard} elevation={0}>
            <div className={classes.metricIcon} style={{ backgroundColor: '#dcfce7', color: '#15803d' }}>
              <AttachMoneyIcon />
            </div>
            <Box>
              <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1.1 }}>
                {formattedCurrency(totalCostSum)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Custo Total da Entrada
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Paper className={classes.summaryPaper} elevation={0}>
        <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#334155', marginBottom: 12 }}>
          Conferência Financeira & Margem Projetada
        </Typography>

        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="textSecondary">
            Total de Itens Prontos para Gravação:
          </Typography>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {totalItems} produto(s) + {childFormatsCount} formato(s) derivado(s)
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="textSecondary">
            Valor de Venda Projetado (Estoque Total):
          </Typography>
          <Typography variant="body2" style={{ fontWeight: 600, color: '#16a34a' }}>
            {formattedCurrency(totalPotentialSale)}
          </Typography>
        </Box>

        <Divider style={{ margin: '12px 0' }} />

        <Box display="flex" justifyContent="space-between">
          <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#1e293b' }}>
            Lucro Bruto Projetado com a Venda:
          </Typography>
          <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#16a34a' }}>
            {formattedCurrency(totalPotentialSale - totalCostSum)}
          </Typography>
        </Box>
      </Paper>

      {/* Banner de Impressão de Etiquetas pós-confirmação */}
      <Paper className={classes.labelPaper} elevation={0}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <div className={classes.metricIcon} style={{ backgroundColor: '#eff6ff', color: '#17497F', padding: 12 }}>
              <PrintIcon style={{ fontSize: 28 }} />
            </div>
            <Box>
              <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#17497F', lineHeight: 1.2 }}>
                Etiquetas de Gôndola & Código de Barras
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 2 }}>
                Gere e imprima etiquetas térmicas ou em folha A4 com os códigos e IDs definitivos após a gravação.
              </Typography>
            </Box>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(printAfterConfirm)}
                onChange={(e) => onTogglePrintAfterConfirm && onTogglePrintAfterConfirm(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" style={{ fontWeight: 600, color: '#17497F' }}>
                  Imprimir etiquetas após confirmação
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Abre o assistente com os dados salvos
                </Typography>
              </Box>
            }
          />
        </Box>
      </Paper>

      {/* Ações Finais */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} flexWrap="wrap" gap={2}>
        <Button
          variant="outlined"
          color="default"
          startIcon={<ArrowBackIcon />}
          onClick={onBackToReview}
          disabled={confirming}
        >
          Voltar para Revisão
        </Button>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Button
            variant="contained"
            color="primary"
            startIcon={confirming ? <CircularProgress size={20} color="inherit" /> : <CheckCircleOutlineIcon />}
            onClick={onConfirmImport}
            disabled={confirming || totalItems === 0}
            style={{ fontWeight: 600, padding: '10px 28px', backgroundColor: '#16a34a' }}
          >
            {confirming ? 'Processando Entrada...' : `Confirmar Importação (${totalItems} itens)`}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ImportStepSummary;
